import { inject, Injectable } from '@angular/core';
import { PrintLabelComponent } from '../components/print-label.component';
import { PrintLabelsComponent } from '../components/print-labels.component';
import { PrintLabelOptions, PrintOptions } from '../models/print-options.model';
import { asArraySafe } from '@pf/shared-utility';
import { LabelType } from '../models/label-types.enum';
import { LabelTemplates } from '../models/label-templates';
import { ContainerDto, EquipmentDto, SlotDto } from '@pf/platform-wms/domain';
import { DrawerService } from '@pf/shared-ui';
import { ItemDto } from '@pf/platform-item-catalog/domain';

@Injectable({
    providedIn: 'root',
})
export class LabelPrintService {
    private readonly drawerService = inject(DrawerService);

    printHandlingUnitLabels(equipment: EquipmentDto) {
        const handlingUnitOptions = asArraySafe(equipment.containers).map(
            container => this.mapContainerToLabelOptions(container)
        );
        this.printLabels({
            labelType: LabelType.HandlingUnit,
            labels: handlingUnitOptions,
            parentLabel: this.mapEquipmentToLabelOptions(equipment),
            widthInches: 4,
            heightInches: 2,
        });
    }

    printSlotLabels(slots: SlotDto[]) {
        const slotLabelOptions = slots.map(slot =>
            this.mapSlotToLabelOptions(slot)
        );
        this.printLabels({
            labelType: LabelType.Slot,
            labels: slotLabelOptions,
            widthInches: 3,
            heightInches: 1,
        });
    }

    printHandlingUnitLabel(container: ContainerDto) {
        this.printLabel({
            labelType: LabelType.HandlingUnit,
            labels: [this.mapContainerToLabelOptions(container)],
        });
    }

    printSlotLabel(slot: SlotDto) {
        this.printLabel({
            labelType: LabelType.Slot,
            labels: [this.mapSlotToLabelOptions(slot)],
            widthInches: 3,
            heightInches: 1,
        });
    }

    printItemLabel(item: ItemDto) {
        this.printLabel({
            labelType: LabelType.Item,
            labels: [this.mapItemToLabelOptions(item)],
        });
    }

    printLabel(printOptions: PrintOptions, onClose?: () => void) {
        this.drawerService.createTemplateDrawer({
            title: `Print Label`,
            content: PrintLabelComponent,
            onClose: onClose,
            size: 'large',
            isMobile: true,
            data: printOptions,
        });
    }

    printLabels(printOptions: PrintOptions, onClose?: () => void) {
        this.drawerService.createTemplateDrawer({
            title: `Print Label`,
            content: PrintLabelsComponent,
            onClose: onClose,
            size: 'large',
            isMobile: false,
            data: printOptions,
        });
    }

    private mapContainerToLabelOptions(
        container: ContainerDto
    ): PrintLabelOptions {
        return {
            barcode: container.lpn as string,
            tags: [container.containerType?.name as string],
            zpl: LabelTemplates[LabelType.HandlingUnit](
                container.lpn as string,
                container.containerType?.name as string
            ),
        };
    }

    private mapEquipmentToLabelOptions(
        equipment: EquipmentDto
    ): PrintLabelOptions {
        return {
            barcode: equipment.barcode as string,
            tags: [equipment.equipmentType?.name as string],
            zpl: LabelTemplates[LabelType.HandlingUnit](
                equipment.barcode as string,
                equipment.equipmentType.name as string
            ),
        };
    }

    private mapSlotToLabelOptions(slot: SlotDto): PrintLabelOptions {
        return {
            barcode: slot.barcode as string,
            tags: [slot.slotType as string],
            zpl: LabelTemplates[LabelType.Slot](slot),
        };
    }

    private mapItemToLabelOptions(item: ItemDto): PrintLabelOptions {
        return {
            barcode: (item.sku || item.code) as string,
            tags: [],
            zpl: LabelTemplates[LabelType.Item](item),
        };
    }
}
